import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import styled, { css } from 'styled-components';
import type { Props as SelectProps } from 'react-select';
import type { CSSProp, FlattenInterpolation, ThemeProps } from 'styled-components';
import type { AllColorType, SizeType, ThemeType } from '../../util/types';
import { ICON_SIZE, Z_INDEXES } from '../../util/vars';
import {
  DEFAULT_TRANSITION_DURATION,
  DEFAULT_TRANSITION_FUNC,
  getFocusStyle,
  getPolishedColors,
} from '../../util/styled';
import {
  ICON_MATERIAL,
  ICON_STUDENT_BACKGROUND,
  ICON_STUDENT_BORDER,
  ICON_WHIZZ,
  PREFIX,
  SELECTOR_ICON_CLEAR,
  SELECTOR_ICON_DROPDOWN,
  SELECTOR_STUDENT_SEARCH,
  SELECTOR_STUDENT_SEARCH_NO_BORDER,
} from '../../util/data-id';

export const getGlobalSelectorStyles = (props: ThemeProps<ThemeType> & SelectProps & { $color: AllColorType }): FlattenInterpolation<ThemeProps<unknown>> => {
  const prefix = props.classNamePrefix;
  const radius = props.theme.radius.input;
  const colors = getPolishedColors(props);
  return css`
    .${prefix}__control {
      color: ${props.theme.colors.dark};
      background-color: ${props.theme.colors.white};
      border: 2px solid ${props.theme.colors.dark};
      border-radius: ${radius};
      transition: box-shadow ${DEFAULT_TRANSITION_DURATION} ${DEFAULT_TRANSITION_FUNC},
        border-color ${DEFAULT_TRANSITION_DURATION} ${DEFAULT_TRANSITION_FUNC};            
                 
      .${prefix}__value-container {
        .${prefix}__placeholder {
          color: ${props.theme.colors.muted};
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-weight: normal;
          padding-right: 2px;
        }
        .${prefix}__multi-value {
          background-color: ${props.theme.colors.light};
        }
      }                  
      &:hover {
        border-color: ${props.theme.colors.primary};
      }         
    }
    .${prefix}__control--is-focused {
      border-color: ${props.theme.colors.primary};
      ${getFocusStyle(props.theme.colors.primary)}
      &:hover {
        border-color: ${props.theme.colors.primary};
      }
    }
    .${prefix}__menu {
        z-index: ${Z_INDEXES.level_9};
        background: ${props.theme.colors.white};
        box-shadow: 0 0 0 1px hsla(0, 0%, 0%, 0.1), 0 4px 11px hsla(0, 0%, 0%, 0.1);
        .${prefix}__menu-list {
          font-weight: normal;
          .${prefix}__option {
            [data-id='${ICON_MATERIAL}'],
            [data-id='${ICON_WHIZZ}'],
            [data-id='${ICON_STUDENT_BORDER}'],
            [data-id='${ICON_STUDENT_BACKGROUND}'] {
              fill: ${colors.most_light.readable} !important;
            }
            &.${prefix}__option--is-focused:not(.${prefix}__option--is-selected) {
              background-color: ${colors.most_light.color};
            }
            &.${prefix}__option--is-selected {
              background-color: ${colors.most_light.color};
              border: 2px solid ${colors.main.color};
              border-width: 2px 0;
              color: ${colors.most_light.readable};
            }
          }
        }
      }
  `;
};

export const getSelectorSizes =
  (props: ThemeProps<ThemeType> & SelectProps & { $hasIcon: boolean, $size: SizeType }): CSSProp => {
    const prefix = props.classNamePrefix;
    return css`
    .${prefix}__control {
      min-height: ${props.theme.sizes[props.$size]};
      .${prefix}__value-container {
        font-size: ${props.theme.fonts[props.$size]};
        padding-top: 0;
        padding-bottom: 0;
        padding-left: ${props.$hasIcon ? `${props.theme.sizes[props.$size]}` : ''};        
      }
      [data-selector='${SELECTOR_ICON_CLEAR}'],
      [data-selector='${SELECTOR_ICON_DROPDOWN}'] {        
        svg {
          width: 100%;
          height: 100%;
        }
      }
      [data-selector='${SELECTOR_ICON_CLEAR}'] {
        margin: 0 4px 0 0;  
        width: calc(${props.theme.sizes[props.$size]}/3);
        height: calc(${props.theme.sizes[props.$size]}/3);
        svg {
          fill: ${props.theme.colors.muted} !important;
        }
      }
      [data-selector='${SELECTOR_ICON_DROPDOWN}'] {
        margin: 0 2px;
        width: calc(${props.theme.sizes[props.$size]}/2);
        height: calc(${props.theme.sizes[props.$size]}/2);
      }
    }
  `;
  };

export const SelectorWrappedStyled = styled('div')<{ $color: AllColorType }>`
  position: relative;
  [data-id='${ICON_MATERIAL}'],
  [data-id='${ICON_STUDENT_BACKGROUND}'],
  [data-id='${ICON_STUDENT_BORDER}'] {
    fill: ${(props) => props.theme.colors[props.$color]} !important;
  }
  
  .${SELECTOR_STUDENT_SEARCH} { 
    .${PREFIX}__control {
      border-radius: ${(props) => props.theme.radius.input};
      box-shadow: ${(props) => props.theme.shadows.md};      
      &.whizz__control--menu-is-open {
        border: 2px solid ${(props) => props.theme.colors.primary};
        border-bottom: 1px;
        border-top-left-radius: ${(props) => props.theme.radius.input};
        border-top-right-radius: ${(props) => props.theme.radius.input};
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
      &.has_value {
        background: ${(props) => props.theme.colors.most_light};
        border-color: ${(props) => props.theme.colors.primary};
        .${PREFIX}__single-value {
          width: 80%;
          color: ${(props) => props.theme.colors.primary};
          font-weight: bold;
          padding-right: 2px;
        }    
        [data-selector='${SELECTOR_ICON_CLEAR}'] svg {
          fill: ${(props) => props.theme.colors.primary} !important;
        }
      }
    }
    .${PREFIX}__indicators {
      width: ${ICON_SIZE}px;
    }
    .${PREFIX}__control {
      input:focus {
        box-shadow: none !important;
        border: none !important;
      }    
    }
    .${PREFIX}__menu {
      margin-top: 0;
      border-radius: 0;
      border: 2px solid ${(props) => props.theme.colors.primary};
      .${PREFIX}__option {
        display: flex;
        align-items: center;        
        font-weight: bold;
        border-bottom: 1px solid ${(props) => props.theme.colors.light};
        height: ${(props) => props.theme.sizes.md};        
      }
      .${PREFIX}__option--is-selected {
        [data-id='${ICON_STUDENT_BACKGROUND}'] {
          fill: ${(props) => props.theme.colors.secondary} !important;
        }
      }
    }
  }
  
  [data-id=${SELECTOR_STUDENT_SEARCH_NO_BORDER}] .${SELECTOR_STUDENT_SEARCH} {
    .${PREFIX}__control {
      border-color: red;
    }
  }
`;

export const SelectorIconStyled = styled('div')<{ $size: SizeType }>`
  display: flex;
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${(props) => `calc(${props.theme.sizes[props.$size]} / 4)`};
  z-index: ${Z_INDEXES.level_1};
  * {
    margin: auto;
  }
`;

export const SelectorStyled = styled(Select)<{ $hasIcon: boolean, $size: SizeType, $color: AllColorType }>`
  ${(props) => getGlobalSelectorStyles(props)}
  ${(props) => getSelectorSizes(props)}  
`;

export const AsyncSelectorStyled = styled(AsyncSelect)<{ $hasIcon: boolean, $size: SizeType, $color: AllColorType }>`
  ${(props) => getGlobalSelectorStyles(props)}
  ${(props) => getSelectorSizes(props)}  
`;

export const CreatableSelectorStyled = styled(CreatableSelect)<{ $hasIcon: boolean, $size: SizeType, $color: AllColorType }>`
  ${(props) => getGlobalSelectorStyles(props)}
  ${(props) => getSelectorSizes(props)}  
`;
